import { useQuery } from '@tanstack/react-query'

import { EntityKeys, QueryType } from 'utils/reactQuery'
import { useAPIV2 } from 'utils/API'
import { usePfStore } from 'components/pages/PurchaseFlowRevamp/store'
import { usePfSessionWorkspaceId } from 'components/pages/PurchaseFlowRevamp/routing/usePfSessionWorkspaceId'
import { useDefaultInvoicingAddress } from 'components/pages/PurchaseFlowRevamp/steps/billing/useDefaultInvoicingAddress'

/**
 * The VAT rate the order will actually be billed at, resolved from the chosen
 * invoicing address.
 *
 * The v2 catalogue carries no VAT rate at all — `CatalogProductV2DTO.price` is
 * documented as excluding VAT and the grouped DTO exposes only `productGroups`
 * and `discountPercentage` — so the cart could quote a net total and nothing
 * else while the backend charged the address-dependent gross. Measured on dev
 * with one cart and only the address changed: an AT address whose VAT number is
 * verified (intra-community reverse charge) produced a payment intent of
 * 154,63 EUR while a DE address produced 184,01 EUR, against a cart that read
 * 154,64 EUR in both cases.
 *
 * Session-scoped like every other v2 endpoint: the client is the SESSION's, so
 * nothing client-identifying rides the request and an admin ordering on a
 * client's behalf cannot be quoted their own rate. The v1
 * `/order/configuration/recalculate-prices` this replaced was the opposite — two
 * handlers split by Accept media type, the client one resolving the caller's
 * principal and the admin one requiring a body `email` it answered 400 without.
 *
 * It also carried per-product prices derived from the singular, channel-blind
 * `findPriceEntry`, so a Propstack / OneSystem client would have been quoted off
 * the base pricebook while their intent and order priced from their channel
 * book. This frontend held that trap back by reading only `vatRate`; the v2
 * endpoint omits the prices entirely, so the convention is no longer load-bearing.
 *
 * `invoicingAddressId` stays a query parameter rather than session state, exactly
 * as the payment intent takes it: it is a selection still in flight on the
 * Billing step, and persisting it would make the cart wait on a write before it
 * could show a rate. Being a query rather than a mutation is what makes
 * out-of-order responses a non-issue — the address is part of the key, so a slow
 * answer for the address the user just left can never overwrite the current one.
 */
enum Endpoints {
  /** GET — the rate the session's order will be billed at. */
  SESSION_VAT = '/workspace/{workspaceId}/sessions/{sessionId}/vat',
}

/** VAT validation status of the address the rate was resolved against. */
type VatValidationStatus = 'VALID' | 'INVALID' | 'NOT_VALIDATED'

interface SessionVatV2DTO {
  /** Percentage, e.g. `19.0`. Zero for a reverse-charged or zero-rated invoice. */
  vatRate: number
  country: string
  vatValidationStatus: VatValidationStatus
}

export interface PfVatResolution {
  /** Percentage, e.g. `19`. Never null once the query has resolved. */
  vatRate: number
  /**
   * True when the rate is 0 because the sale is reverse-charged to a verified
   * EU VAT number, rather than because the product is genuinely zero-rated.
   * The cart says so — a bare "VAT (0.00%)" is accurate and tells the client
   * nothing about why they are not being charged it.
   */
  isReverseCharged: boolean
}

/**
 * Resolves the VAT rate for the current session + selected (or sole saved) invoicing address.
 *
 * Workspace resolution matches `useCatalogue` / `useInstructionOptions` /
 * `usePfMeasurementPricing`, so the backend's session↔workspace guard never sees
 * a mismatch. Self-gates on workspace, session and address: it stays idle on
 * every step before Billing, where no address has been chosen and there is
 * therefore no honest rate to show.
 */
export function usePfVatRate() {
  const api = useAPIV2<Endpoints>()

  const sessionId = usePfStore((s) => s.draft.sessionId)
  // Billing highlights a sole saved address without writing it; the gates and placement accept it.
  const defaultInvoicingAddressId = useDefaultInvoicingAddress()?.id
  const invoicingAddressId = usePfStore((s) => s.invoicingAddress?.id) ?? defaultInvoicingAddressId

  const ws = usePfSessionWorkspaceId()

  return useQuery({
    queryKey: [EntityKeys.PF_REVAMP_VAT, QueryType.GET, { ws, sessionId, invoicingAddressId }],
    queryFn: () => api.get<SessionVatV2DTO>(
      Endpoints.SESSION_VAT,
      { workspaceId: ws ?? '', sessionId: sessionId ?? '' },
      false,
      {
        params: { invoicingAddressId },
      }
    ),
    select: (dto): PfVatResolution => ({
      vatRate: dto.vatRate,
      isReverseCharged: dto.vatRate === 0 && dto.vatValidationStatus === 'VALID',
    }),
    enabled: !!ws && !!sessionId && !!invoicingAddressId,
  })
}
